import * as React from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { flatten } from "lodash";
import { Container, Loader, Tab } from "semantic-ui-react";

import {
    PpqManagerSection,
    QUESTIONNAIRE_KEY as MANAGER_SECTION_QUESTIONNAIRE_KEY,
    detailsOfCandidateSectionId
} from "@teamprevent/common/ppq/components/PpqManagerSection";
import {
    PpqEmployeeSection,
    QUESTIONNAIRE_KEY as EMPLOYEE_SECTION_QUESTIONNAIRE_KEY,
    candidateDetailsSectionId
} from "@teamprevent/common/ppq/components/PpqEmployeeForm";
import { QuestionnaireConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { AnsweredQuestion, AnsweredQuestionWithId, Covid19FeatureType } from "@teamprevent/common/model";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { clearQuestionnaire as clearQuestionnaireAction, submitInvalidQuestionnaire, updateQuestion } from "@teamprevent/common/questionnaires/actions";
import * as managerSectionQuestions from "@teamprevent/common/questionnaires/questionnaireConfig/ppqManagerSection/questionIds";
import * as employeeSectionQuestions from "@teamprevent/common/questionnaires/questionnaireConfig/ppqEmployeeSection/questionIds";

import { useDispatch } from "@teamprevent/admin-redux";

import { routeIdSelector as customerIdSelector, customerSelector } from "../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector, employeeRecordSelector } from "../../crud/employeeRecord/selectors";
import { CREATE_PPQ } from "../actionTypes";
import { loadOrgStructures } from "../../crud/orgStructure";
import { EmployeeRecord } from "../../crud/employeeRecord";
import { createPpq } from "../actions";
import { selectAndMergeEmployeeSectionQuestionnaireConfig, selectAndMergeManagerSectionQuestionnaireConfig } from "../selectors";
import { ApplicationState } from "../../applicationState";
import { Customer } from "../../crud/customer";
import { evidenceApiUrl } from "../models";
import { loadWorkGroups as loadWorkGroupsAction, WorkGroup } from "../../crud/workGroup";
import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";

import { loadQuestionnaireConfig } from "../../questionnaireConfig/actions";
import { loadRecruitmentManagers } from "../../referrals/actions";

import { push } from "redux-little-router";

interface StateProps {
    customerId: string;
    customer: Customer;
    customerWorkGroups: WorkGroup[];
    employeeId: string;
    employee: EmployeeRecord;
    managerSectionQuestionnaireConfig: QuestionnaireConfig;
    employeeSectionQuestionnaireConfig: QuestionnaireConfig;
    managerSectionAnsweredQuestions: AnsweredQuestionWithId[];
    employeeSectionAnsweredQuestions: AnsweredQuestionWithId[];
}

interface DispatchProps {
    submit: (customerId: string, employeeId: string, managerSection: AnsweredQuestionWithId[], employeeSection: AnsweredQuestionWithId[]) => Promise<string>;
    submitInvalid: (questionnaireKey: string) => void;
    goToOhCase: (customerId: string, employeeId: string, ohCaseId: string) => void;
    loadOrgStructures: (customerId: string, parentOrgStructureId?: string) => void;
    loadWorkGroups: (customerId: string) => void;
    updateAnswer: (id: string, question: AnsweredQuestion, questionnaireKey: string, sectionId: string) => void;
    clearQuestionnaire: () => void;
}

const PpqFormUnconnected: React.FunctionComponent<StateProps & DispatchProps> = ({ clearQuestionnaire, loadWorkGroups, customerId, ...props }) => {
    const dispatch = useDispatch();

    const { customMedicalTypes, isLoaded } = useCustomMedicalTypes({ enable: true });

    React.useEffect(() => {
        (async () => {
            await props.loadOrgStructures(customerId);
            setManagerSectionAnswer(managerSectionQuestions.ReferredTitle, props.employee.title);
            setManagerSectionAnswer(managerSectionQuestions.ReferredFirstName, props.employee.firstName);
            setManagerSectionAnswer(managerSectionQuestions.ReferredLastName, props.employee.lastName);
            setManagerSectionAnswer(managerSectionQuestions.ReferredDob, props.employee.dateOfBirth?.format("YYYY-MM-DD"));
            setManagerSectionAnswer(managerSectionQuestions.PersonalEmailAddress, props.employee.personalEmailAddress);
            setManagerSectionAnswer(managerSectionQuestions.ConfirmPersonalEmailAddress, props.employee.personalEmailAddress);
            setManagerSectionAnswer(managerSectionQuestions.Postcode, props.employee.postCode);
            setManagerSectionAnswer(managerSectionQuestions.JobTitle, props.employee.jobTitle);
            setManagerSectionAnswer(managerSectionQuestions.EmployeeId, props.employee.secondaryReference);
            setManagerSectionAnswer(managerSectionQuestions.AnticipatedStartDate, props.employee.anticipatedStartDate?.format("YYYY-MM-DD"));
            setManagerSectionAnswer(managerSectionQuestions.OrgLevel1Name, props.employee.orgStructureOne && props.employee.orgStructureOne.name);
            setManagerSectionAnswer(managerSectionQuestions.OrgLevel2Name, props.employee.orgStructureTwo && props.employee.orgStructureTwo.name);
            setManagerSectionAnswer(managerSectionQuestions.OrgLevel3Name, props.employee.orgStructureThree && props.employee.orgStructureThree.name);
            setManagerSectionAnswer(managerSectionQuestions.OrgLevel4Name, props.employee.orgStructureFour && props.employee.orgStructureFour.name);
            setEmployeeSectionAnswer(employeeSectionQuestions.Title, props.employee.title);
            setEmployeeSectionAnswer(employeeSectionQuestions.FirstName, props.employee.firstName);
            setEmployeeSectionAnswer(employeeSectionQuestions.LastName, props.employee.lastName);
            setEmployeeSectionAnswer(employeeSectionQuestions.Gender, props.employee.gender);
            setEmployeeSectionAnswer(employeeSectionQuestions.Dob, props.employee.dateOfBirth?.format("YYYY-MM-DD"));
            setEmployeeSectionAnswer(employeeSectionQuestions.AddressLine1, props.employee.addressLine1);
            setEmployeeSectionAnswer(employeeSectionQuestions.AddressLine2, props.employee.addressLine2);
            setEmployeeSectionAnswer(employeeSectionQuestions.City, props.employee.city);
            setEmployeeSectionAnswer(employeeSectionQuestions.County, props.employee.county);
            setEmployeeSectionAnswer(employeeSectionQuestions.Postcode, props.employee.postCode);
            setEmployeeSectionAnswer(employeeSectionQuestions.MobilePhone, props.employee.mobilePhoneNumber);
            setEmployeeSectionAnswer(employeeSectionQuestions.HomePhone, props.employee.homePhoneNumber);
        })();
    }, [props.managerSectionQuestionnaireConfig, props.employeeSectionQuestionnaireConfig]);

    React.useEffect(() => {
        if (customerId) {
            loadWorkGroups(customerId);
        }
    }, [customerId, loadWorkGroups]);

    React.useEffect(() => {
        clearQuestionnaire();
    }, [clearQuestionnaire]);

    React.useEffect(() => {
        dispatch(loadQuestionnaireConfig({ customerId, questionnaireType: "ppqEmployeeSection" }));
        dispatch(loadQuestionnaireConfig({ customerId, questionnaireType: "ppqManagerSection" }));
        dispatch(loadRecruitmentManagers({ customerId }));
    }, [customerId, dispatch]);

    const showCovid19Questions = React.useMemo(() => {
        const covid19FeaturesCount = props.customer?.covid19Features?.indexOf(Covid19FeatureType.PPQ_RISK_ASSESSMENT);

        if (covid19FeaturesCount === undefined) {
            return false;
        }
        return covid19FeaturesCount > -1;
    }, [props.customer?.covid19Features]);

    const setManagerSectionAnswer = (questionId: string, answer?: string) => {
        if (!props.managerSectionQuestionnaireConfig) {
            return;
        }

        const questionConfig =
            props.managerSectionQuestionnaireConfig.questions.find(q => q.id === questionId) as CustomQuestionConfig;
        props.updateAnswer(
            questionConfig.id,
            { question: questionConfig.questionText, sectionId: detailsOfCandidateSectionId, id: questionId, answer, isValid: true },
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);
    };

    const setEmployeeSectionAnswer = (questionId: string, answer?: string) => {
        if (!props.employeeSectionQuestionnaireConfig) {
            return;
        }
        const questionConfig =
            props.employeeSectionQuestionnaireConfig.questions.find(q => q.id === questionId) as CustomQuestionConfig;
        props.updateAnswer(
            questionConfig.id,
            { question: questionConfig.questionText, sectionId: candidateDetailsSectionId, id: questionId, answer, isValid: true },
            EMPLOYEE_SECTION_QUESTIONNAIRE_KEY,
            candidateDetailsSectionId);
    };

    const trimAnswerAndUpdateEmployeeQuestion = (
        questionId: string,
        questionnaireQuestions: AnsweredQuestionWithId[],
        questions: AnsweredQuestionWithId[],
        questionnaireKey: string,
        sectionId: string) => {

        const stateQuestion = questionnaireQuestions.find(q => q.id === questionId);

        if (stateQuestion) {
            props.updateAnswer(
                stateQuestion.id,
                { ...stateQuestion, answer: stateQuestion.answer?.toString()?.trim() },
                questionnaireKey,
                sectionId
            );
        }

        const question = questions.find(q => q.id === questionId);

        if (question) {
            question.answer = question.answer?.toString()?.trim();
        }
    };

    const onSubmit = React.useCallback(async () => {

        const managerSectionAnsweredQuestions = [...props.managerSectionAnsweredQuestions];
        const employeeSectionAnsweredQuestions = [...props.employeeSectionAnsweredQuestions];

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.ReferredTitle,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.ReferredFirstName,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.ReferredLastName,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.PersonalEmailAddress,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.ConfirmPersonalEmailAddress,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            managerSectionQuestions.JobTitle,
            props.managerSectionAnsweredQuestions,
            managerSectionAnsweredQuestions,
            MANAGER_SECTION_QUESTIONNAIRE_KEY,
            detailsOfCandidateSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            employeeSectionQuestions.Title,
            props.employeeSectionAnsweredQuestions,
            employeeSectionAnsweredQuestions,
            EMPLOYEE_SECTION_QUESTIONNAIRE_KEY,
            candidateDetailsSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            employeeSectionQuestions.FirstName,
            props.employeeSectionAnsweredQuestions,
            employeeSectionAnsweredQuestions,
            EMPLOYEE_SECTION_QUESTIONNAIRE_KEY,
            candidateDetailsSectionId);

        trimAnswerAndUpdateEmployeeQuestion(
            employeeSectionQuestions.LastName,
            props.employeeSectionAnsweredQuestions,
            employeeSectionAnsweredQuestions,
            EMPLOYEE_SECTION_QUESTIONNAIRE_KEY,
            candidateDetailsSectionId);

        if (props.managerSectionAnsweredQuestions.some(q => !q.isValid) || props.employeeSectionAnsweredQuestions.some(q => !q.isValid)) {
            props.submitInvalid(MANAGER_SECTION_QUESTIONNAIRE_KEY);
            props.submitInvalid(EMPLOYEE_SECTION_QUESTIONNAIRE_KEY);
            return;
        }
        const ohCaseId = await props.submit(customerId, props.employeeId, managerSectionAnsweredQuestions, employeeSectionAnsweredQuestions);
        props.goToOhCase(customerId, props.employeeId, ohCaseId);
    }, [customerId, props, trimAnswerAndUpdateEmployeeQuestion]);

    const panes = [
        { menuItem: "Manager Section", pane: (
            <Tab.Pane key="Manager Section">
                <PpqManagerSection
                    readonly={false}
                    questionnaireConfig={props.managerSectionQuestionnaireConfig}
                    hideSubmitButton
                    loadOrgStructure={(parentOrgStructureLevelId) => props.loadOrgStructures(customerId, parentOrgStructureLevelId)}
                    showCovid19Questions={showCovid19Questions}
                    customerWorkGroups={props.customerWorkGroups}
                    customMedicalTypes={customMedicalTypes}
                />
            </Tab.Pane>
        ) },
        { menuItem: "Employee Section", pane: (
            <Tab.Pane key="Employee Section">
                <PpqEmployeeSection
                    readonly={false}
                    questionnaireConfig={props.employeeSectionQuestionnaireConfig}
                    hideSubmitButton
                    evidenceApiUrl={evidenceApiUrl}
                />
            </Tab.Pane>
        ) }
    ];

    if (!isLoaded) {
        return <Loader active />;
    }

    return (
        <Container>
            <h2>Pre-placement Questionnaire</h2>
            {props.managerSectionQuestionnaireConfig && props.employeeSectionQuestionnaireConfig ?
                <>
                    <Tab panes={panes} renderActiveOnly={false} />
                    <Container textAlign="center"><IsLoadingButton requestNames={CREATE_PPQ} content="Submit" onClick={onSubmit} /></Container>
                </>:
                <Loader active />
            }
        </Container>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const managerForm = state.questionnaires[MANAGER_SECTION_QUESTIONNAIRE_KEY];
    const employeeForm = state.questionnaires[EMPLOYEE_SECTION_QUESTIONNAIRE_KEY];

    return {
        customerId: customerIdSelector(state),
        customer: customerSelector(state),
        customerWorkGroups: state.workGroups,
        employeeId: employeeRecordIdSelector(state),
        employee: employeeRecordSelector(state),
        managerSectionQuestionnaireConfig: selectAndMergeManagerSectionQuestionnaireConfig(state),
        employeeSectionQuestionnaireConfig: selectAndMergeEmployeeSectionQuestionnaireConfig(state),
        managerSectionAnsweredQuestions: managerForm ? flatten(managerForm.sections.map(section => Object.values(section.questions))) : [],
        employeeSectionAnsweredQuestions: employeeForm ? flatten(employeeForm.sections.map(section => Object.values(section.questions))) : []
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>): DispatchProps => {
    return {
        submit: (customerId: string, employeeId: string, managerSection: AnsweredQuestionWithId[], employeeSection: AnsweredQuestionWithId[]) =>
            dispatch(createPpq(customerId, employeeId, managerSection, employeeSection)),
        goToOhCase: (customerId, employeeId, ohCaseId) => dispatch(push(`/customers/${customerId}/employeeRecords/${employeeId}/ohCases/${ohCaseId}`)),
        submitInvalid: (questionnaireKey) => dispatch(submitInvalidQuestionnaire(questionnaireKey, true)),
        loadOrgStructures: (customerId, parentOrgStructureId) => dispatch(loadOrgStructures({ customerId, parentOrgStructureId })),
        loadWorkGroups: (customerId) => dispatch(loadWorkGroupsAction({ customerId })),
        updateAnswer: (id, question, questionnaireKey, sectionId) => dispatch(updateQuestion({ id, question, questionnaireKey, sectionId })),
        clearQuestionnaire: () => dispatch(clearQuestionnaireAction(MANAGER_SECTION_QUESTIONNAIRE_KEY))
    };
};

export const PpqForm = connect(mapStateToProps, mapDispatchToProps)(PpqFormUnconnected);
